<template>
  <main-layout>
    <v-container>
      <h1 class="mb-3">{{ $t('branches_management') }}</h1>

      <BranchFormCreate v-if="isFormOpen">
        <BranchForm
          :value="editedForm"
          :type="formType"
          :name="formName"
          @cancel="isFormOpen = false"
          @submit="handleFormSubmit"
        />
      </BranchFormCreate>
      <template v-else>
        <div :style="{ margin: '-0.25rem' }">
          <v-layout wrap justify-end>
            <v-flex shrink class="pa-1">
              <v-btn color="primary" class="ma-0" @click="isSearchOpen = !isSearchOpen">
                <v-icon>search</v-icon>
                {{ $t('Search') }}
                <v-icon v-if="!isSearchOpen">expand_more</v-icon>
                <v-icon v-else>expand_less</v-icon>
              </v-btn>
            </v-flex>
          </v-layout>
        </div>

        <BranchSearch
          v-if="isSearchOpen"
          v-model="filter"
          class="mt-3"
          @input="isSearchOpen = false"
        />

        <v-btn
          v-if="!isSearchOpen"
          block
          outline
          large
          color="primary"
          class="mt-3 mx-0 mb-0"
          :style="{ borderStyle: 'dashed', height: '64px' }"
          @click="add"
        >
          <v-icon>add</v-icon>
          {{ $t('branch_add') }}
        </v-btn>
      </template>

      <div class="mt-5" :style="{ margin: '-0.25rem' }">
        <v-layout wrap justify-end>
          <v-flex shrink class="pa-1">
            <v-btn outline color="grey" :loading="isExporting" class="ma-0" @click="exportData">
              <v-icon>ios_share</v-icon>
              {{ $t('export') }}
            </v-btn>
          </v-flex>
        </v-layout>
      </div>

      <div class="mt-3">
        <v-pagination
          v-if="total > 0"
          :value="filter.page"
          :length="totalPages"
          class="mb-2"
          @input="filter = { ...filter, page: $event }"
        />
        <div v-if="!hasInited" class="d-flex justify-center align-center">
          <v-progress-circular color="primary" indeterminate />
        </div>
        <div v-else-if="hasInited && total > 0" class="branch-list">
          <BranchItem
            v-for="item in items"
            :key="item.id"
            :value="item"
            @activeChange="handleActiveChange"
            @edited="handleEdited"
            @delete="handleDelete"
          />
        </div>
        <p v-else class="mb-0 text-xs-center">{{ $t('no_data') }}</p>
        <v-pagination
          v-if="total > 0"
          :value="filter.page"
          :length="totalPages"
          class="mt-2"
          @input="filter = { ...filter, page: $event }"
        />
      </div>
    </v-container>
  </main-layout>
</template>

<script>
import BranchSearch from './Search.vue'
import BranchFormCreate from './FormCreate.vue'
import BranchForm from './Form.vue'
import BranchItem from './Item.vue'
import superrichApi from '@/api/superrich'
import { mapGetters } from 'vuex'
import XLSX from 'xlsx'

export default {
  components: { BranchSearch, BranchFormCreate, BranchForm, BranchItem },
  data() {
    return {
      items: [],
      total: 0,
      filter: {
        page: 1,
        limit: 10
      },
      hasInited: false,
      isSearchOpen: false,
      isFormOpen: false,
      isExporting: false
    }
  },
  computed: {
    ...mapGetters({
      selectedBot: 'selectedBot'
    }),
    totalPages() {
      return Math.ceil(this.total / this.filter.limit)
    },
    formType() {
      if (this.editedForm) {
        return 'edit'
      }
      return 'add'
    },
    formName() {
      if (!this.editedForm) {
        return ''
      }
      return this.editedForm.branchName
    }
  },
  watch: {
    filter: {
      immediate: true,
      handler() {
        this.fetchItems()
      }
    }
  },
  methods: {
    async fetchItems() {
      try {
        if (this.hasInited) {
          this.$store.state.loading = true
        }

        const response = await superrichApi.getBranches({
          page_id: this.selectedBot.pageid,
          limit: this.filter.limit,
          offset: (this.filter.page - 1) * this.filter.limit,
          sort_by: 'id',
          sort_type: 'desc',
          branch_name: this.filter.branchName,
          phone_number: this.filter.phoneNumber,
          subdistrict: this.filter.subdistrict,
          district: this.filter.district,
          province: this.filter.province,
          postcode: this.filter.postcode,
          latitude: this.filter.latitude,
          longitude: this.filter.longitude
        })

        if (response.status === 200 && response.data.data) {
          this.items = response.data.data
          this.total = response.data.total
        }
      } catch (error) {
        console.error(error)
      } finally {
        this.$store.state.loading = false
        this.hasInited = true
      }
    },
    add() {
      this.editedId = null
      this.editedForm = null
      this.isFormOpen = true
    },
    async exportData() {
      try {
        this.isExporting = true

        const response = await superrichApi.getBranches({
          page_id: this.selectedBot.pageid,
          limit: 1000000,
          offset: 0,
          sort_by: 'id',
          sort_type: 'desc',
          branch_name: this.filter.branchName,
          phone_number: this.filter.phoneNumber,
          subdistrict: this.filter.subdistrict,
          district: this.filter.district,
          province: this.filter.province,
          postcode: this.filter.postcode,
          latitude: this.filter.latitude,
          longitude: this.filter.longitude
        })

        if (response.status === 200 && response.data.data) {
          const rows = response.data.data.map(
            ({
              branch_name,
              address,
              subdistrict,
              district,
              province,
              postcode,
              phone_number,
              latitude,
              longitude
            }) => ({
              ชื่อสาขา: branch_name,
              ที่อยู่: address,
              'แขวง/ตำบล': subdistrict,
              'เขต/อำเภอ': district,
              จังหวัด: province,
              รหัสไปรษณีย์: postcode,
              เบอร์โทร: phone_number,
              ละติจูด: latitude,
              ลองจิจูด: longitude
            })
          )

          const worksheet = XLSX.utils.json_to_sheet(rows)
          const workbook = XLSX.utils.book_new()
          XLSX.utils.book_append_sheet(workbook, worksheet, 'Branches')
          XLSX.writeFile(workbook, `branches-${Date.now()}.xlsx`)
        }
      } catch (error) {
        console.error(error)
      } finally {
        this.isExporting = false
      }
    },
    async handleFormSubmit({
      branchName,
      address,
      phoneNumber,
      latitude,
      longitude,
      googleMapUrl,
      district,
      subdistrict,
      province,
      postcode,
      keywords
    }) {
      const { value: isConfirmed } = await this.$swal({
        title: this.$t('branch_add_question', { name: branchName }),
        type: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('Confirm'),
        cancelButtonText: this.$t('cancel'),
        reverseButtons: true
      })

      if (!isConfirmed) return

      const subdistrictPrefix = province === 'กรุงเทพมหานคร' ? 'แขวง' : 'ตำบล'
      const districtPrefix = province === 'กรุงเทพมหานคร' ? 'เขต' : 'อำเภอ'
      const provincePrefix = province === 'กรุงเทพมหานคร' ? '' : 'จังหวัด'
      const fullAddress = [
        address,
        `${subdistrictPrefix}${subdistrict}`,
        `${districtPrefix}${district}`,
        `${provincePrefix}${province}`,
        postcode
      ].join(' ')

      try {
        this.$store.state.loading = true

        const response = await superrichApi.postBranch({
          page_id: this.selectedBot.pageid,
          branch_name: branchName,
          address: address,
          phone_number: phoneNumber,
          latitude: latitude,
          longitude: longitude,
          link_google_map: googleMapUrl,
          district: district,
          subdistrict: subdistrict,
          province: province,
          postcode: postcode,
          address_string: fullAddress,
          keywords: keywords.map(({ text }) => text)
        })

        if (response.status === 204) {
          this.filter = { page: 1, limit: 10 }
          this.isFormOpen = false
        }
      } catch (error) {
        console.error(error)
      } finally {
        this.$store.state.loading = false
      }
    },
    async handleActiveChange(item, value, { setIsActive, setIsActiveLoading }) {
      try {
        setIsActiveLoading(true)

        const response = await superrichApi.putBranch({
          page_id: this.selectedBot.pageid,
          id: item.id,
          is_used: String(value)
        })

        if (response.status === 204) {
          this.$swal({
            title: value ? this.$t('branch_enable_success') : this.$t('branch_disable_success'),
            type: 'success'
          })
        }
      } catch (error) {
        console.error(error)
        setIsActive(item.is_used)
      } finally {
        setIsActiveLoading(false)
      }
    },
    handleEdited() {
      this.filter = { ...this.filter }
    },
    handleEdit({
      id,
      branch_name,
      phone_number,
      link_google_map,
      address,
      subdistrict,
      district,
      province,
      postcode,
      latitude,
      longitude
    }) {
      this.editedId = id
      this.editedForm = {
        branchName: branch_name,
        phoneNumber: phone_number,
        googleMapUrl: link_google_map,
        address: address,
        subdistrict: subdistrict,
        district: district,
        province: province,
        postcode: postcode,
        latitude: +latitude,
        longitude: +longitude
      }
      this.isFormOpen = true
    },
    async handleDelete({ id, branch_name }) {
      const { value: isConfirmed } = await this.$swal({
        title: this.$t('branch_delete_question', { name: branch_name }),
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('Confirm'),
        cancelButtonText: this.$t('cancel'),
        reverseButtons: true
      })

      if (isConfirmed) {
        try {
          this.$store.state.loading = true

          const response = await superrichApi.deleteBranch({
            page_id: this.selectedBot.pageid,
            id
          })

          if (response.status === 204) {
            this.filter = { ...this.filter }
          }
        } catch (error) {
          console.error(error)
        } finally {
          this.$store.state.loading = false
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.branch-list {
  > *:not(:first-child) {
    margin-top: 0.5rem;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/view/template/superrich/branch/Branch.vue